import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Button, Header, Item, Segment, Image, Grid, Icon, Modal, Loader, Label, Popup } from 'semantic-ui-react';
import { Game } from '../../../app/models/Game';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useStore } from '../../../app/stores/store';
import GameFollowButton from './GameFollowButton';
import { GameLockDTO } from '../../../app/models/GameLockDTO';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { formatDateDisplay } from '../../../app/common/util/dateUtils';
import QRCode from 'qrcode';

// ─── CTA Discord-style Animation ─────────────────────────────────────────────
const CTA_STYLES = `
  @keyframes cta-shimmer-once {
    0%   { transform: translateX(-100%) skewX(-15deg); opacity: 1; }
    100% { transform: translateX(250%)  skewX(-15deg); opacity: 1; }
  }

  @keyframes cta-arrow-bounce {
    0%, 100% { transform: translateX(0); }
    30%       { transform: translateX(4px); }
    60%       { transform: translateX(-2px); }
  }

  .cta-discord-btn {
    position: relative !important;
    overflow: hidden !important;
    transition: background 0.18s ease, transform 0.15s ease, box-shadow 0.18s ease !important;
  }

  .cta-discord-btn::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 40%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
    animation: cta-shimmer-once 0.75s ease-out 0.3s 1 both;
    pointer-events: none;
  }

  .cta-discord-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 14px rgba(0, 181, 173, 0.35) !important;
  }

  .cta-discord-btn:active {
    transform: translateY(0px) scale(0.98) !important;
    transition: transform 0.08s ease !important;
  }

  .cta-discord-btn:hover .cta-arrow {
    animation: cta-arrow-bounce 0.5s ease !important;
    display: inline-block;
  }
`;

// ─── Static styles ────────────────────────────────────────────────────────────
const gameImageStyle = {
  filter: 'brightness(40%)',
};

const gameImageTextStyle = {
  position: 'absolute',
  bottom: '5%',
  left: '5%',
  width: '100%',
  height: 'auto',
  color: 'white',
};

interface Props {
  game: Game;
}

export default observer(function GameDetailedHeader({ game }: Props) {
  const { t } = useTranslation();

  const getLocalizedSport = (sportName: string) => {
    const key = `sports.${sportName.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? sportName : translated;
  };

  const {
    gameStore: {
      updateAttendence,
      loading,
      loadingDeleteGame,
      deleteGame,
      updateGameLocking,
      gameLockLoading,
    },
  } = useStore();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const navigate = useNavigate();

  const currentUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

  React.useEffect(() => {
    const generateQRCode = async () => {
      try {
        const qrDataUrl = await QRCode.toDataURL(currentUrl, {
          width: 80,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        });
        setQrCodeDataUrl(qrDataUrl);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
      }
    };
    generateQRCode();
  }, [currentUrl]);

  const handleDeleteGame = async () => {
    await deleteGame(game.id);
    setConfirmOpen(false);
    navigate('/games');
  };

  const handleLockToggle = () => {
    const gameLockDTO: GameLockDTO = {
      ID: game.id,
      isLocked: !game.isLocked,
    };
    updateGameLocking(gameLockDTO);
  };

  const handleViberShare = () => {
    const fullUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const sportName = getLocalizedSport(game.sport?.sportName || '');
    const dateTime = `${formatDateDisplay(game.dateTimeStart!)}  ${format(game.dateTimeStart!, 'h:mm aa')}`;
    const message = `${t('gameDetailedHeader.inviteMessage')} 🎮 ${game.gameName}
🏆 ${sportName}
📅 ${dateTime}

${fullUrl}`;
    const viberUrl = `viber://forward?text=${encodeURIComponent(message)}`;
    window.open(viberUrl, '_blank');
  };

  const handleWhatsAppShare = () => {
    const fullUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const sportName = getLocalizedSport(game.sport?.sportName || '');
    const dateTime = `${formatDateDisplay(game.dateTimeStart!)}  ${format(game.dateTimeStart!, 'h:mm aa')}`;
    const message = `🎮 ${game.gameName}
🏆 ${sportName}
📅 ${dateTime}

${fullUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const sportLabelTextStyle = {
    fontSize: '1rem',
    color: 'white',
    fontWeight: 'bold',
    textShadow: '2px 2px 5px rgba(0,0,0,0.3)',
    textTransform: 'uppercase',
    letterSpacing: '2px',
  } as const;

  // ─── Derived flags ──────────────────────────────────────────────────────────
  const isPast = game.isGameInThePast;

  return (
    <>
      <style>{CTA_STYLES}</style>

      <Segment.Group>
        <Segment
          basic
          attached='top'
          style={{
            padding: '0',
            position: 'relative',
            background: `linear-gradient(rgba(24,42,115,0.8), rgba(33,138,174,0.7), rgba(32,167,172,0.8)), url('/assets/categoryImages/${game.sport?.sportName}.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center 30%',
            backgroundRepeat: 'no-repeat',
            borderRadius: '5px 5px 0 0',
            overflow: 'hidden',
          }}
        >
          {/* QR Code and Share Buttons */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {qrCodeDataUrl && (
              <Popup
                trigger={
                  <div
                    style={{
                      backgroundColor: 'white',
                      padding: '8px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={qrCodeDataUrl}
                      alt='QR Code'
                      style={{ display: 'block', width: '80px', height: '80px' }}
                    />
                  </div>
                }
                content={
                  t('gameDetailedHeader.scanQRCode') ||
                  'Scan QR code to share this game'
                }
                position='bottom left'
                inverted
              />
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                flexWrap: 'wrap',
                maxWidth: '96px',
              }}
            >
              {/* WhatsApp */}
              <Popup
                trigger={
                  <button
                    onClick={handleWhatsAppShare}
                    style={{
                      backgroundColor: '#25D366',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 8px rgba(0,0,0,0.2)';
                    }}
                  >
                    <Icon name='whatsapp' size='large' style={{ margin: 0 }} />
                  </button>
                }
                content={
                  t('gameDetailedHeader.shareWhatsApp') || 'Share via WhatsApp'
                }
                position='bottom left'
                inverted
              />

              {/* Viber */}
              <Popup
                trigger={
                  <button
                    onClick={handleViberShare}
                    style={{
                      backgroundColor: '#7360F2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '45px',
                      height: '45px',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 8px rgba(0,0,0,0.2)';
                    }}
                  >
                    <Icon
                      name='telegram plane'
                      size='large'
                      style={{ margin: 0 }}
                    />
                  </button>
                }
                content={
                  t('gameDetailedHeader.shareViber') || 'Share via Viber'
                }
                position='bottom left'
                inverted
              />
            </div>
          </div>

          {/* Sport Label */}
          <div
            style={{
              width: '100%',
              height: '150px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              position: 'relative',
            }}
          >
            <Icon name='trophy' size='large' />
            <span style={sportLabelTextStyle}>
              {getLocalizedSport(game.sport?.sportName || '')}
            </span>

            <Image
              src={`/assets/categoryImages/${game.sport?.sportName}.jpg`}
              circular
              style={{
                position: 'absolute',
                right: '20px',
                top: '100%',
                transform: 'translateY(-50%)',
                width: '200px',
                height: '200px',
                objectFit: 'cover',
                border: '3px solid rgba(255,255,255,0.8)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                opacity: '0.70',
              }}
            />
          </div>

          <Grid stackable style={{ margin: 0, padding: '12px' }}>
            <Grid.Row style={{ paddingBottom: '8px' }}>
              <Grid.Column width={16}>
                <Header
                  size='medium'
                  content={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Icon
                        name='gamepad'
                        style={{ marginRight: '8px', color: 'white' }}
                      />
                      <span style={{ wordBreak: 'break-word', color: 'white' }}>
                        {game.gameName}
                      </span>
                    </div>
                  }
                  style={{ margin: '0 0 8px 0' }}
                />
              </Grid.Column>
            </Grid.Row>

            <Grid.Row style={{ paddingTop: 0, paddingBottom: '4px' }}>
              {/* Date/Time */}
              <Grid.Column mobile={16} tablet={8} computer={6}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    color: 'white',
                    marginBottom: '8px',
                  }}
                >
                  <Icon
                    name='calendar outline'
                    style={{ marginTop: '2px', flexShrink: 0, color: 'white' }}
                  />
                  <div style={{ marginLeft: '6px', fontSize: '1em' }}>
                    <div style={{ fontSize: '1.5em', fontWeight: 'bold' }}>
                      {formatDateDisplay(game.dateTimeStart!)}
                    </div>
                    <div style={{ fontSize: '1.5em', opacity: 0.9 }}>
                      {format(game.dateTimeStart!, 'h:mm aa')}
                    </div>
                  </div>
                </div>
              </Grid.Column>

              {/* Host */}
              <Grid.Column mobile={16} tablet={8} computer={10}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px',
                  }}
                >
                  <Icon
                    name='user circle'
                    color='yellow'
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    style={{
                      color: 'white',
                      fontSize: '1em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('gameDetailedHeader.hostedBy')}
                  </span>
                  <Label
                    as={Link}
                    to={`/profiles/${game.host?.userID}`}
                    size='medium'
                    style={{
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: '1px solid #2980b9',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <Icon name='star outline' />
                    {game.host?.fullName}
                  </Label>
                </div>
              </Grid.Column>
            </Grid.Row>

            {isPast && (
              <Grid.Row style={{ paddingTop: '4px' }}>
                <Grid.Column width={16}>
                  <Label color='grey' size='medium' style={{ opacity: 0.8 }}>
                    <Icon name='history' />
                    {t('gameDetailedHeader.pastGame')}
                  </Label>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* Follow Button */}
        {!isPast && (
          <Segment>
            <GameFollowButton game={game} />
          </Segment>
        )}

        <Segment clearing attached='bottom'>
          <Grid stackable>
            {/* Attendance Buttons */}
            {!isPast && (
              <Grid.Column width={8} textAlign='left'>
                {game.isGoing ? (
                  <Button
                    fluid
                    size='small'
                    loading={loading}
                    onClick={updateAttendence}
                    disabled={false}
                  >
                    {t('gameDetailedHeader.cancelAttendance')}
                  </Button>
                ) : (
                  <Button
                    fluid
                    size='small'
                    loading={loading}
                    onClick={updateAttendence}
                    color='teal'
                    disabled={game.isLocked}
                    // CTA animacija samo kad je meč otvoren za prijavu
                    className={!game.isLocked ? 'cta-discord-btn' : undefined}
                  >
                    {t('gameDetailedHeader.joinGame')}
                    {!game.isLocked && (
                      <>{' '}<span className='cta-arrow'>→</span></>
                    )}
                  </Button>
                )}

                <div style={{ marginTop: '10px' }}>
                  {game.isLocked ? (
                    <span style={{ color: 'red' }}>
                      <Icon name='lock' /> {t('gameDetailedHeader.gameLocked')}
                    </span>
                  ) : (
                    <span style={{ color: 'green' }}>
                      <Icon name='unlock' />{' '}
                      {t('gameDetailedHeader.gameOpen')}
                    </span>
                  )}
                </div>
              </Grid.Column>
            )}

            {/* Host Controls */}
            {game.isHost && (
              <Grid.Column width={8} textAlign='right'>
                {!isPast && (
                  <>
                    <Button
                      color={game.isLocked ? 'green' : 'yellow'}
                      onClick={handleLockToggle}
                      loading={gameLockLoading}
                      style={{ marginBottom: '5px' }}
                      fluid
                    >
                      <Icon name={game.isLocked ? 'unlock' : 'lock'} />
                      {game.isLocked
                        ? t('gameDetailedHeader.unlockGame')
                        : t('gameDetailedHeader.lockGame')}
                    </Button>

                    <Button
                      color='orange'
                      as={Link}
                      to={`/manage/${game.id}`}
                      style={{ marginBottom: '5px' }}
                      fluid
                    >
                      <Icon name='edit' />
                      {t('gameDetailedHeader.manageGame')}
                    </Button>

                    <Button
                      color='red'
                      onClick={() => setConfirmOpen(true)}
                      fluid
                    >
                      <Icon name='trash' />
                      {t('gameDetailedHeader.deleteGame')}
                    </Button>
                  </>
                )}
              </Grid.Column>
            )}
          </Grid>

          {/* Delete Confirmation Modal */}
          <Modal
            size='mini'
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            dimmer='blurring'
          >
            <Modal.Header
              style={{
                backgroundColor: '#f8f8f9',
                borderBottom: '1px solid #d4d4d5',
              }}
            >
              {t('gameDetailedHeader.deleteGameTitle')}
            </Modal.Header>
            <Modal.Content>
              <p>{t('gameDetailedHeader.deleteGameConfirmation')}</p>
              {loadingDeleteGame && (
                <div style={{ textAlign: 'center', margin: '15px 0' }}>
                  <Loader active inline size='small' />
                  <span style={{ marginLeft: '10px', color: '#767676' }}>
                    {t('gameDetailedHeader.deletingGame')}
                  </span>
                </div>
              )}
            </Modal.Content>
            <Modal.Actions
              style={{
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #d4d4d5',
              }}
            >
              <Button
                onClick={() => setConfirmOpen(false)}
                disabled={loadingDeleteGame}
              >
                {t('gameDetailedHeader.cancel')}
              </Button>
              <Button
                negative
                content={t('gameDetailedHeader.delete')}
                icon='trash'
                labelPosition='right'
                onClick={handleDeleteGame}
                loading={loadingDeleteGame}
                disabled={loadingDeleteGame}
              />
            </Modal.Actions>
          </Modal>
        </Segment>
      </Segment.Group>
    </>
  );
});